/** Canonical display order for account financial_data metrics. */
export const FINANCIAL_METRIC_ORDER = [
  'Annual revenue',
  'EBITDA',
  'EBITDA margin',
  'SDE',
  'Net income',
  'Operating income',
  'Cash-flow positivity',
  'Capex indicators',
  'Debt indicators',
  'Organic revenue growth',
  'Recurring revenue share',
  'Subscriber estimate',
];

export const FINANCIAL_FOOTNOTE_METRIC = 'Quality of financial evidence';

export const FINANCIAL_CONFIDENCE_LABELS = {
  H: 'High',
  M: 'Medium',
  L: 'Low',
};

export const FINANCIAL_CONFIDENCE_TOOLTIPS = {
  H: 'Audited, SEC filing, company-published, or official registry source',
  M: 'Broker-provided, official dataset, or press-verified source',
  L: 'Seller-stated or single-source information',
};

/** Normalize financial_data from account payload (JSONB object or JSON string). */
export function normalizeFinancialData(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return null;
}

export function hasAccountFinancials(account) {
  const data = normalizeFinancialData(account?.financial_data);
  return !!data && Object.keys(data).length > 0;
}

export function hasTextField(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Tab visibility: financial metrics or key assets. */
export function hasAccountFinancialsTab(account) {
  if (hasAccountFinancials(account)) return true;
  if (hasTextField(account?.key_assets)) return true;
  return false;
}

/** Parse key_assets lines as Label: detail rows for the financials table. */
export function parseKeyAssetTableRows(keyAssets) {
  if (!hasTextField(keyAssets)) return [];

  const rows = [];

  for (const line of keyAssets.split(/\r?\n/)) {
    const stripped = line.trim().replace(/^•\s*/, '').trim();
    if (!stripped) continue;

    const colonIdx = stripped.indexOf(': ');
    if (colonIdx === -1) {
      rows.push({
        label: stripped,
        value: '—',
        isWatchItem: /^watch item/i.test(stripped),
      });
      continue;
    }

    const label = stripped.slice(0, colonIdx).trim();
    const value = stripped.slice(colonIdx + 2).trim();
    rows.push({
      label,
      value: value || '—',
      isWatchItem: /^watch item/i.test(label),
    });
  }

  return rows;
}

/** @deprecated Use parseKeyAssetTableRows — kept for any legacy callers. */
export function parseKeyAssetLines(keyAssets) {
  const rows = parseKeyAssetTableRows(keyAssets);
  const bullets = rows.filter((r) => !r.isWatchItem).map((r) =>
    r.value === '—' ? r.label : `${r.label}: ${r.value}`,
  );
  const watchItems = rows
    .filter((r) => r.isWatchItem)
    .map((r) => (r.value === '—' ? r.label : `${r.label}: ${r.value}`));
  return { bullets, watchItems };
}

/** Split selling_rationale into blank-line-separated paragraphs. */
export function parseSellingRationaleParagraphs(value) {
  if (!hasTextField(value)) return [];
  return value
    .trim()
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export function getOrderedFinancialMetrics(financialData) {
  const data = normalizeFinancialData(financialData);
  if (!data) return [];

  const keys = Object.keys(data);
  const orderSet = new Set(FINANCIAL_METRIC_ORDER);
  const withoutFootnote = keys.filter((key) => key !== FINANCIAL_FOOTNOTE_METRIC);
  const known = FINANCIAL_METRIC_ORDER.filter((key) => withoutFootnote.includes(key));
  const extra = withoutFootnote.filter((key) => !orderSet.has(key));

  const rows = [...known, ...extra].map((key) => ({
    key,
    ...(data[key] && typeof data[key] === 'object' ? data[key] : { value: data[key] }),
  }));

  if (keys.includes(FINANCIAL_FOOTNOTE_METRIC)) {
    const footnote = data[FINANCIAL_FOOTNOTE_METRIC];
    rows.push({
      key: FINANCIAL_FOOTNOTE_METRIC,
      ...(footnote && typeof footnote === 'object' ? footnote : { value: footnote }),
      _isFootnote: true,
    });
  }

  return rows;
}

function formatFullUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatCompactUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');

  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);

  if (abs >= 1_000_000) {
    const millions = abs / 1_000_000;
    const decimals = millions >= 10 ? 0 : 1;
    return `${sign}$${millions.toFixed(decimals)}M`;
  }
  if (abs >= 1_000) {
    const thousands = abs / 1_000;
    const decimals = thousands >= 10 ? 0 : 1;
    return `${sign}$${thousands.toFixed(decimals)}K`;
  }
  return formatFullUsd(n);
}

export function formatFinancialMetricValue(entry) {
  const { value, unit } = entry || {};
  if (value === null || value === undefined || value === '') return '—';

  if (unit === 'USD') {
    return {
      display: formatCompactUsd(value),
      tooltip: formatFullUsd(value),
    };
  }
  if (unit === '%') {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      return { display: String(value), tooltip: null };
    }
    const display = `${n.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
    return { display, tooltip: null };
  }
  if (unit === 'count') {
    const n = Number(value);
    const display = Number.isFinite(n)
      ? n.toLocaleString('en-US')
      : String(value);
    return { display, tooltip: null };
  }
  return { display: String(value), tooltip: null };
}
